<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { User, Search } from 'lucide-vue-next'

interface Tutor {
  id: number
  name: string
  slug: string
  avatar_url: string | null
  subject: string
  status: string
  color?: string
}

defineProps<{
  tutors: Tutor[]
}>()

function getBorderColor(index: number): string {
  const colors = [
    'border-blue-600',
    'border-green-600',
    'border-purple-600',
    'border-teal-500',
    'border-orange-500',
  ]
  return colors[index % colors.length]
}
</script>

<template>
  <section>
    <div class="flex items-center justify-between mb-3">
      <h2 class="text-lg font-bold text-gray-900">My Tutors</h2>
    </div>
    <div class="bg-white rounded-xl border border-gray-200 p-4">
      <!-- Tutors List -->
      <div v-if="tutors.length > 0" class="flex items-center space-x-6">
        <Link
          v-for="(tutor, index) in tutors"
          :key="tutor.id"
          :href="`/student/tutors/${tutor.slug}`"
          class="flex flex-col items-center hover:opacity-80 transition-opacity"
        >
          <div
            :class="[
              'w-14 h-14 rounded-full border-2 mb-2 overflow-hidden bg-gray-100 flex items-center justify-center',
              tutor.status === 'pending' ? 'border-amber-400 opacity-70' : getBorderColor(index)
            ]"
          >
            <img
              v-if="tutor.avatar_url"
              :src="tutor.avatar_url"
              :alt="tutor.name"
              class="w-full h-full object-cover"
            />
            <User v-else class="w-7 h-7 text-gray-400" />
          </div>
          <p class="text-xs font-semibold text-gray-900 text-center">{{ tutor.name }}</p>
          <p v-if="tutor.status === 'pending'" class="text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Pending</p>
          <p v-else class="text-xs text-gray-500">{{ tutor.subject }}</p>
        </Link>
      </div>

      <!-- Empty State - Find Tutors -->
      <div v-else class="py-6 text-center">
        <div class="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
          <Search class="w-8 h-8 text-blue-600" />
        </div>
        <h3 class="text-sm font-semibold text-gray-900 mb-1">Find Your Tutors</h3>
        <p class="text-xs text-gray-500 mb-4">Discover expert tutors in your subjects of interest</p>
        <Link
          href="/student/explore"
          class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Search class="w-4 h-4 mr-2" />
          Browse Tutors
        </Link>
      </div>
    </div>
  </section>
</template>
